import { getSiteConfig, type AllowedDepositPaymentMethods, type AllowedWithdrawPaymentMethods, type PaymentMethod, type WithdrawPaymentMethod } from '@shared/config';
import { AppStore, Bank, Card, Codesandbox, PayByBank, PaysafeCard } from '@shared/icons';

type PaymentMethodWithIcon<T extends PaymentMethod | WithdrawPaymentMethod> = T & {
  icon: React.ReactNode;
};

/**
 * Map of (global) icons for deposit methods
 * It ensures icons are consistent across the apps
 */
const depositMethodsIconsMap = {
  APPLE_PAY: <AppStore size="medium" />,
  CARD: <Card size="medium" />,
  PAYBYBANK: <PayByBank size="medium" className="w-full" />,
  PAYSAFECARD: <PaysafeCard size="medium" />,
  PAYMENTIQHUB: <Codesandbox size="medium" />,
} satisfies Record<AllowedDepositPaymentMethods, React.ReactNode>;

/**
 * Map of (global) icons for withdraw methods
 * It ensures icons are consistent across the apps
 */
const withdrawMethodsIconsMap = {
  BANK_TRANSFER: <Bank size="medium" />,
} satisfies Record<AllowedWithdrawPaymentMethods, React.ReactNode>;

const {
  paymentMethodsConfig: { depositMethods, withdrawMethods },
} = getSiteConfig();

function objectKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

const DEPOSIT_PAYMENT_METHODS = Object.fromEntries(
  objectKeys(depositMethods).map(methodKey => [
    methodKey,
    {
      ...depositMethods[methodKey],
      icon: depositMethodsIconsMap[methodKey],
    } satisfies PaymentMethodWithIcon<PaymentMethod>,
  ])
) as Record<AllowedDepositPaymentMethods, PaymentMethodWithIcon<PaymentMethod>>;

const WITHDRAW_PAYMENT_METHODS = Object.fromEntries(
  objectKeys(withdrawMethods).map(methodKey => [
    methodKey,
    {
      ...withdrawMethods[methodKey],
      icon: withdrawMethodsIconsMap[methodKey],
    } satisfies PaymentMethodWithIcon<WithdrawPaymentMethod>,
  ])
) as Record<AllowedWithdrawPaymentMethods, PaymentMethodWithIcon<WithdrawPaymentMethod>>;

export { DEPOSIT_PAYMENT_METHODS, WITHDRAW_PAYMENT_METHODS };
